import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    FaRegEnvelope,
    FaWhatsapp,
    FaLinkedin,
    FaInstagram,
    FaCheckCircle,
    FaGraduationCap,
} from 'react-icons/fa';
import Navigation from '../components/Navigation/Navigation';
import Footer from '../components/Footer/Footer';
import {
    BodyContainer,
    Container,
} from '../components/StyledComponents/StyledComponents';
import { getAuthorData } from '../data/Authors';
import { supportedLanguages } from '../i18n';
import {
    CANONICAL_BLOG_BASE,
    PUBLIC_BLOG_BASE,
    SITE_ORG_URL,
} from '../config/siteConfig';

const ProfileContainer = styled.div`
    min-height: 70vh;
`;

const ProfileHeader = styled.div`
    display: flex;
    gap: 2rem;
    align-items: flex-start;
    margin-bottom: 4rem;
    padding: 2.5rem;
    background: ${(props) =>
        props.theme.mode === 'light' ? '#f8f9fa' : '#2a2a2a'};
    border-radius: 12px;
    border-left: 4px solid ${(props) => props.theme.colors.primary};

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 2rem;
        gap: 1.5rem;
    }
`;

const ProfileImage = styled.img`
    width: 140px;
    height: 140px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid ${(props) => props.theme.colors.primary};
    flex-shrink: 0;

    @media (max-width: 768px) {
        width: 120px;
        height: 120px;
    }
`;

const ProfileInfo = styled.div`
    flex: 1;
`;

const ProfileName = styled.h1`
    margin: 0 0 0.5rem 0;
    font-size: 2rem;
    color: ${(props) => props.theme.body.headers};
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 1.75rem;
    }
`;

const ProfileTitle = styled.h2`
    margin: 0 0 1rem 0;
    font-size: 1.15rem;
    color: ${(props) => props.theme.colors.primary};
    font-weight: 500;
`;

const ProfileBio = styled.p`
    margin: 0 0 1.25rem 0;
    line-height: 1.7;
    color: ${(props) => props.theme.body.text};
    font-size: 0.95rem;
`;

const SocialLinks = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const SocialLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    background: ${(props) => props.theme.colors.primary};
    color: white !important;
    text-decoration: none;
    border-radius: 25px;
    font-weight: 500;
    transition: all 0.3s ease;
    font-size: 0.9rem;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        opacity: 0.9;
    }

    i,
    svg {
        font-size: 1.1rem;
        width: 1.2rem;
        height: 1.2rem;
        flex-shrink: 0;
    }
`;

const Section = styled.section`
    margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.8rem;
    color: ${(props) => props.theme.body.headers};
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 3px solid ${(props) => props.theme.colors.primary};
    font-weight: 600;
`;

const LongBioText = styled.div`
    line-height: 1.9;
    color: ${(props) => props.theme.body.text};
    font-size: 1.05rem;

    p {
        margin-bottom: 1.5rem;
    }
`;

const SpecialtiesList = styled.ul`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    list-style: none;
    padding: 0;
    margin: 0;
`;

const SpecialtyItem = styled.li`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    background: ${(props) =>
        props.theme.mode === 'light' ? '#f8f9fa' : '#2a2a2a'};
    border-radius: 8px;
    color: ${(props) => props.theme.body.text};
    border-left: 3px solid ${(props) => props.theme.colors.primary};
    font-weight: 500;

    i,
    svg {
        color: ${(props) => props.theme.colors.primary};
        font-size: 1.2rem;
    }
`;

const CredentialsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const CredentialItem = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
    margin-bottom: 1rem;
    background: ${(props) =>
        props.theme.mode === 'light' ? '#f8f9fa' : '#2a2a2a'};
    border-radius: 8px;
    color: ${(props) => props.theme.body.text};
    font-size: 1.05rem;
    border-left: 3px solid ${(props) => props.theme.colors.primary};

    i,
    svg {
        color: ${(props) => props.theme.colors.primary};
        font-size: 1.3rem;
        margin-top: 0.2rem;
    }
`;

const ContactSection = styled.div`
    background: ${(props) =>
        props.theme.mode === 'light' ? '#f0f8f7' : '#1a1a1a'};
    border: 2px solid ${(props) => props.theme.colors.primary};
    border-radius: 12px;
    padding: 2.5rem;
    text-align: center;
    margin-top: 4rem;
`;

const ContactTitle = styled.h3`
    font-size: 1.6rem;
    color: ${(props) => props.theme.body.headers};
    margin-bottom: 1rem;
    font-weight: 600;
`;

const ContactText = styled.p`
    color: ${(props) => props.theme.body.text};
    margin-bottom: 2rem;
    font-size: 1.05rem;
    line-height: 1.6;
`;

const ContactButtons = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
`;

const ContactButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background: ${(props) => props.theme.colors.primary};
    color: white !important;
    text-decoration: none;
    border-radius: 30px;
    font-weight: 600;
    font-size: 1.05rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }

    i,
    svg {
        font-size: 1.3rem;
    }
`;

const NotFound = styled.div`
    text-align: center;
    padding: 4rem 2rem;
    color: ${(props) => props.theme.body.text};

    h2 {
        font-size: 2rem;
        color: ${(props) => props.theme.body.headers};
        margin-bottom: 1rem;
    }

    p {
        font-size: 1.1rem;
        margin-bottom: 2rem;
    }

    a {
        color: ${(props) => props.theme.colors.primary};
        text-decoration: none;
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const AuthorProfile = () => {
    const { authorId, lang } = useParams();
    const { t, i18n } = useTranslation();
    const [author, setAuthor] = useState(null);
    const [loading, setLoading] = useState(true);
    const language = supportedLanguages.includes(lang)
        ? lang
        : i18n.language || 'en';
    const publicBase = PUBLIC_BLOG_BASE;
    const canonicalBase = CANONICAL_BLOG_BASE;

    useEffect(() => {
        const authorData = getAuthorData(authorId, language);
        setAuthor(authorData);
        setLoading(false);
    }, [authorId, language]);

    if (loading) {
        return (
            <>
                <Navigation />
                <BodyContainer>
                    <Container small top={12}>
                        <ProfileContainer>
                            <p
                                style={{
                                    textAlign: 'center',
                                    padding: '4rem 0',
                                }}
                            >
                                {t('authorProfile.loading')}
                            </p>
                        </ProfileContainer>
                    </Container>
                </BodyContainer>
                <Footer />
            </>
        );
    }

    if (!author) {
        return (
            <>
                <Navigation />
                <BodyContainer>
                    <Container small top={12}>
                        <ProfileContainer>
                            <NotFound>
                                <h2>{t('authorProfile.notFound')}</h2>
                                <p>{t('authorProfile.notFoundText')}</p>
                                <a href={`${publicBase}/${language}`}>
                                    {t('authorProfile.backToBlog')}
                                </a>
                            </NotFound>
                        </ProfileContainer>
                    </Container>
                </BodyContainer>
                <Footer />
            </>
        );
    }

    // Generate Person Schema for SEO
    const personSchema = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: author.name,
        jobTitle: author.jobTitle,
        description: author.bio,
        email: author.email,
        url: `${canonicalBase}/${language}/guias/${author.id}`,
        image: author.image,
        worksFor: {
            '@type': 'Organization',
            name: 'Dizen',
            url: SITE_ORG_URL,
        },
        sameAs: Object.values(author.social || {}),
    };

    return (
        <>
            <Helmet>
                <title>{`${author.name} - ${author.jobTitle} | Dizen Blog`}</title>
                <meta name="description" content={author.bio} />
                <meta
                    name="keywords"
                    content={`${
                        author.name
                    }, guía turismo Buenos Aires, ${author.specialties.join(
                        ', '
                    )}`}
                />
                <link
                    rel="canonical"
                    href={`${canonicalBase}/${language}/guias/${author.id}`}
                />

                {/* Open Graph */}
                <meta
                    property="og:title"
                    content={`${author.name} - ${author.jobTitle}`}
                />
                <meta property="og:description" content={author.bio} />
                <meta property="og:image" content={author.image} />
                <meta
                    property="og:url"
                    content={`${canonicalBase}/${language}/guias/${author.id}`}
                />
                <meta property="og:type" content="profile" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={`${author.name} - ${author.jobTitle}`}
                />
                <meta name="twitter:description" content={author.bio} />
                <meta name="twitter:image" content={author.image} />

                {/* JSON-LD Schema */}
                <script type="application/ld+json">
                    {JSON.stringify(personSchema)}
                </script>
            </Helmet>

            <Navigation />

            <BodyContainer>
                <Container small top={12}>
                    <ProfileContainer>
                        <ProfileHeader>
                            <ProfileImage
                                src={author.image}
                                alt={author.name}
                                loading="eager"
                            />
                            <ProfileInfo>
                                <ProfileName>{author.name}</ProfileName>
                                <ProfileTitle>{author.jobTitle}</ProfileTitle>
                                <ProfileBio>{author.bio}</ProfileBio>
                                <SocialLinks>
                                    {author.email && (
                                        <SocialLink
                                            href={`mailto:${author.email}`}
                                        >
                                            <FaRegEnvelope />
                                            {t('authorProfile.email')}
                                        </SocialLink>
                                    )}
                                    {author.whatsapp && (
                                        <SocialLink
                                            href={`https://wa.me/${author.whatsapp}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaWhatsapp />
                                            WhatsApp
                                        </SocialLink>
                                    )}
                                    {author.social?.linkedin && (
                                        <SocialLink
                                            href={author.social.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaLinkedin />
                                            LinkedIn
                                        </SocialLink>
                                    )}
                                    {author.social?.instagram && (
                                        <SocialLink
                                            href={author.social.instagram}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaInstagram />
                                            Instagram
                                        </SocialLink>
                                    )}
                                </SocialLinks>
                            </ProfileInfo>
                        </ProfileHeader>

                        <Section>
                            <SectionTitle>
                                {t('authorProfile.about')}
                            </SectionTitle>
                            <LongBioText>
                                {author.longBio
                                    .split('\n\n')
                                    .map((paragraph, index) => (
                                        <p key={index}>{paragraph}</p>
                                    ))}
                            </LongBioText>
                        </Section>

                        <Section>
                            <SectionTitle>
                                {t('authorProfile.specialties')}
                            </SectionTitle>
                            <SpecialtiesList>
                                {author.specialties.map((specialty, index) => (
                                    <SpecialtyItem key={index}>
                                        <FaCheckCircle />
                                        {specialty}
                                    </SpecialtyItem>
                                ))}
                            </SpecialtiesList>
                        </Section>

                        {author.credentials && (
                            <Section>
                                <SectionTitle>
                                    {t('authorProfile.credentials')}
                                </SectionTitle>
                                <CredentialsList>
                                    {author.credentials.map(
                                        (credential, index) => (
                                            <CredentialItem key={index}>
                                                <FaGraduationCap />
                                                <span>{credential}</span>
                                            </CredentialItem>
                                        )
                                    )}
                                </CredentialsList>
                            </Section>
                        )}

                        <ContactSection>
                            <ContactTitle>
                                {t('authorProfile.contactTitle')}
                            </ContactTitle>
                            <ContactText>
                                {t('authorProfile.contactText')}
                            </ContactText>
                            <ContactButtons>
                                <ContactButton href={`mailto:${author.email}`}>
                                    <FaRegEnvelope />
                                    {t('authorProfile.sendEmail')}
                                </ContactButton>
                                {author.whatsapp && (
                                    <ContactButton
                                        href={`https://wa.me/${author.whatsapp}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FaWhatsapp />
                                        {t('authorProfile.sendWhatsApp')}
                                    </ContactButton>
                                )}
                            </ContactButtons>
                        </ContactSection>
                    </ProfileContainer>
                </Container>
            </BodyContainer>

            <Footer />
        </>
    );
};

export default AuthorProfile;
